import { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { adminApi, apiErrorMessage } from "../../api/adminApi";
import { MODULES, ACTIONS } from "../../permissions/modules";
import Can from "../../permissions/Can";
import "../../main.scss";
import Tooltip from "../../components/Tooltip";

const blankPerms = () => {
  const p = {};
  MODULES.forEach((m) => {
    p[m.key] = { view: false, add: false, edit: false, delete: false };
  });
  return p;
};

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [perms, setPerms] = useState(blankPerms());
  const [loading, setLoading] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });

  const selected = roles.find((r) => r.id === selectedId);

  const loadRoles = useCallback(
    async (keepSelection = true) => {
      try {
        const r = await adminApi.getRoles();
        setRoles(r);
        setSelectedId((prev) =>
          !keepSelection || !prev ? (r.length ? r[0].id : null) : prev
        );
      } catch (e) {
        toast.error(apiErrorMessage(e, "فشل تحميل الأدوار"));
      }
      setLoading(false);
    },
    []
  );

  const loadPerms = async (id) => {
    setLoadingPerms(true);
    try {
      const p = await adminApi.getRolePermissions(id);
      setPerms(p);
    } catch (e) {
      toast.error(apiErrorMessage(e, "فشل تحميل الصلاحيات"));
    }
    setLoadingPerms(false);
  };

  useEffect(() => {
    loadRoles(false);
  }, [loadRoles]);

  useEffect(() => {
    if (selectedId) loadPerms(selectedId);
  }, [selectedId]);

  const toggle = (moduleKey, action) => {
    setPerms((prev) => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], [action]: !prev[moduleKey][action] },
    }));
  };

  const toggleRow = (moduleKey) => {
    const row = perms[moduleKey];
    const allOn = ACTIONS.every((a) => row[a.key]);
    const next = {};
    ACTIONS.forEach((a) => {
      next[a.key] = !allOn;
    });
    setPerms((prev) => ({ ...prev, [moduleKey]: next }));
  };

  const toggleColumn = (action) => {
    const allOn = MODULES.every((m) => perms[m.key][action]);
    setPerms((prev) => {
      const next = { ...prev };
      MODULES.forEach((m) => {
        next[m.key] = { ...next[m.key], [action]: !allOn };
      });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateRolePermissions(selectedId, perms);
      toast.success("تم حفظ الصلاحيات");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSaving(false);
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!roleForm.name.trim()) {
      toast.error("اسم الدور مطلوب");
      return;
    }
    try {
      const res = await adminApi.createRole({
        name: roleForm.name.trim(),
        description: roleForm.description.trim(),
      });
      toast.success("تم إنشاء الدور");
      setShowModal(false);
      setRoleForm({ name: "", description: "" });
      await loadRoles(false);
      setSelectedId(res.id);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`حذف الدور "${role.name}" نهائياً؟`)) return;
    try {
      await adminApi.deleteRole(role.id);
      toast.success("تم حذف الدور");
      setSelectedId(null);
      loadRoles(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div className="page roles-page">
      <div className="page__header">
        <div>
          <h1 className="page__title">الأدوار والصلاحيات</h1>
          <p className="page__sub">حدد ما يستطيع كل دور رؤيته وتنفيذه في النظام</p>
        </div>
        <Can module="roles" action="add">
          <button className="btn btn--primary" onClick={() => setShowModal(true)}>
            <i className="fa-solid fa-plus"></i> دور جديد
          </button>
        </Can>
      </div>

      {loading ? (
        <div className="page__loading">جاري التحميل...</div>
      ) : (
        <div className="roles-layout">
          <aside className="roles-list">
            {roles.map((r) => (
              <button
                key={r.id}
                className={`role-item ${r.id === selectedId ? "active" : ""}`}
                onClick={() => setSelectedId(r.id)}
              >
                <span className="role-item__main">
                  <span className="role-item__name">
                    {r.name}
                    {r.is_system ? (
                      <span className="sys-badge">نظامي</span>
                    ) : null}
                  </span>
                  <span className="role-item__count">
                    {r.users_count} مستخدم
                  </span>
                </span>
                <Can module="roles" action="delete">
                  {!r.is_system && (
                    <Tooltip content="حذف الدور">
                      <span
                        className="role-del"
                     
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRole(r);
                        }}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </span>
                    </Tooltip>
                  )}
                </Can>
              </button>
            ))}
          </aside>

          <section className="roles-matrix-card">
            {!selected ? (
              <div className="page__loading">اختر دوراً لعرض صلاحياته</div>
            ) : loadingPerms ? (
              <div className="page__loading">جاري تحميل الصلاحيات...</div>
            ) : (
              <>
                <div className="matrix-head">
                  <div>
                    <h2>{selected.name}</h2>
                    {selected.description && <p>{selected.description}</p>}
                  </div>
                  <Can module="roles" action="edit">
                    <button
                      className="btn btn--primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "جاري الحفظ..." : "حفظ الصلاحيات"}
                    </button>
                  </Can>
                </div>
                <div className="matrix-scroll">
                  <table className="matrix-table">
                    <thead>
                      <tr>
                        <th>الوحدة</th>
                        {ACTIONS.map((a) => (
                          <th key={a.key}>
                            <button
                              className="col-toggle"
                              onClick={() => toggleColumn(a.key)}
                              title={`تبديل الكل: ${a.label}`}
                            >
                              {a.label}
                            </button>
                          </th>
                        ))}
                        <th>الكل</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MODULES.map((m) => {
                        const row = perms[m.key] || {};
                        const allOn = ACTIONS.every((a) => row[a.key]);
                        return (
                          <tr key={m.key}>
                            <td className="mod-label">{m.label}</td>
                            {ACTIONS.map((a) => (
                              <td key={a.key} className="cell">
                                <button
                                  className={`perm-check ${row[a.key] ? "on" : ""}`}
                                  onClick={() => toggle(m.key, a.key)}
                                  aria-label={`${m.label} - ${a.label}`}
                                >
                                  {row[a.key] && (
                                    <i className="fa-solid fa-check"></i>
                                  )}
                                </button>
                              </td>
                            ))}
                            <td className="cell">
                              <Tooltip content="تبديل الصف">
                                <button
                                  className={`perm-check all ${allOn ? "on" : ""}`}
                                  onClick={() => toggleRow(m.key)}
                               
                                >
                                  {allOn && <i className="fa-solid fa-check"></i>}
                                </button>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="matrix-hint">
                  اضغط على اسم العمود لتبديل الصلاحية لكل الوحدات، وعلى زر
                  "الكل" لتبديل صف كامل
                </p>
              </>
            )}
          </section>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>دور جديد</h3>
            <form onSubmit={handleCreateRole}>
              <label>
                اسم الدور
                <input
                  value={roleForm.name}
                  onChange={(e) =>
                    setRoleForm({ ...roleForm, name: e.target.value })
                  }
                  placeholder="مثال: محاسب"
                  required
                />
              </label>
              <label>
                الوصف
                <input
                  value={roleForm.description}
                  onChange={(e) =>
                    setRoleForm({ ...roleForm, description: e.target.value })
                  }
                  placeholder="وصف اختياري"
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setShowModal(false)}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn btn--primary">
                  إنشاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .roles-page { padding: 20px; max-width: 1200px; margin: 0 auto; }
        .page__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
        .page__title { margin: 0 0 4px; font-size: 22px; color: var(--text); }
        .page__sub { margin: 0; color: var(--text-muted); font-size: 13px; }
        .page__loading { text-align: center; padding: 60px; color: var(--text-muted); }
        .roles-layout { display: grid; grid-template-columns: 260px 1fr; gap: 16px; align-items: start; }
        .roles-list { display: flex; flex-direction: column; gap: 8px; }
        .role-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; background: var(--card); border: 1px solid var(--border); border-radius: 6px; padding: 12px 14px; cursor: pointer; text-align: right; }
        .role-item.active { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent); }
        .role-item__main { display: flex; flex-direction: column; gap: 2px; }
        .role-item__name { color: var(--text); font-size: 14px; display: flex; align-items: center; gap: 8px; }
        .sys-badge { font-size: 10px; background: rgba(201,149,44,0.15); color: var(--gold); padding: 2px 8px; border-radius: 6px; }
        .role-item__count { color: var(--text-muted); font-size: 12px; }
        .role-del { color: var(--text-muted); padding: 6px; }
        .role-del:hover { color: #dc2626; }
        .roles-matrix-card { background: var(--card); border: 1px solid var(--border); border-radius: 6px; padding: 18px; }
        .matrix-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
        .matrix-head h2 { margin: 0 0 4px; font-size: 18px; color: var(--text); }
        .matrix-head p { margin: 0; color: var(--text-muted); font-size: 13px; }
        .matrix-scroll { overflow-x: auto; }
        .matrix-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 560px; }
        .matrix-table th { color: var(--text-muted); font-weight: 400; padding: 8px; border-bottom: 1px solid var(--border); }
        .matrix-table td { padding: 8px; border-bottom: 1px solid var(--border); }
        .mod-label { color: var(--text); white-space: nowrap; }
        .cell { text-align: center; }
        .col-toggle { background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 13px; font-family: inherit; padding: 4px 8px; border-radius: 6px; }
        .col-toggle:hover { color: var(--accent-strong, #1d7567); background: rgba(40,145,127,0.1); }
        .perm-check { width: 30px; height: 30px; border-radius: 6px; border: 1.5px solid var(--border); background: var(--bg); cursor: pointer; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
        .perm-check.on { background: var(--accent); border-color: var(--accent); }
        .perm-check.all.on { background: var(--gold); border-color: var(--gold); }
        .matrix-hint { color: var(--text-muted); font-size: 12px; margin: 12px 0 0; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(13,35,32,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
        .modal-card { background: var(--card); border-radius: 6px; padding: 22px; width: 100%; max-width: 400px; }
        .modal-card h3 { margin: 0 0 16px; color: var(--text); font-size: 17px; }
        .modal-card label { display: block; margin-bottom: 12px; font-size: 13px; color: var(--text); }
        .modal-card input { width: 100%; margin-top: 6px; padding: 10px 12px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--text); font-size: 14px; }
        .modal-actions { display: flex; justify-content: flex-start; gap: 10px; margin-top: 18px; }
        @media (max-width: 860px) { .roles-layout { grid-template-columns: 1fr; } .roles-list { flex-direction: row; overflow-x: auto; } .role-item { min-width: 180px; } }
      `}</style>
    </div>
  );
}
